import { DocumentNode, gql } from "@apollo/client";

class PostBillService {
  // ===== Guest entry =====

  public static publicLogin = (): DocumentNode => gql`
    mutation PublicLogin($input: PublicLoginInput!) {
      publicLogin(input: $input) {
        patient {
          uid
          firstName
          lastName
        }
        token
      }
    }
  `;

  public static guestValidation = (): DocumentNode => gql`
    mutation PostbillGuestValidation($token: String!, $input: DOBDataInput!) {
      postbillGuestValidation(token: $token, input: $input) {
        token
      }
    }
  `;

  // ===== Logged-in entry =====

  public static verifyLogin = (): DocumentNode => gql`
    query VerifyLogin {
      verifyLogin {
        token
        displayBillingCenter
      }
    }
  `;

  // ===== CTS-authenticated queries =====

  public static getItems = (): DocumentNode => gql`
    query GetPostbillItems($ctsToken: String!) {
      getPostbillItems(ctsToken: $ctsToken) {
        patientUid
        patientName
        totalBalance
        balanceDateStatus
        displayDate {
          dueDate
          pastDueExists
        }
        pastDueExists
        pastDueType
        hasInternationalClinic
        readyToPay {
          orderUid
          lineItemUid
          clinicId
          testName {
            fullName
            shortName
          }
          balance
          dueDate {
            dueDate
            pastDueExists
          }
          dateOfService
          statementStatus
          orderedBy
          caseNumber
          currency
          hasPdf
        }
        upcoming {
          orderUid
          lineItemUid
          clinicId
          testName {
            fullName
            shortName
          }
          balance
          dueDate {
            dueDate
            pastDueExists
          }
          dateOfService
          statementStatus
          orderedBy
          caseNumber
          currency
          hasPdf
        }
        readyToPayInstallments {
          numInstallments
          currentInstallmentIndex
          installmentAmount
          dueDate
          caseNumber
          remainingBalance
          paymentMethod {
            brand
            last4digits
            expiry
          }
          testName
          paymentPlanId
          status
          currency
        }
        upcomingInstallments {
          numInstallments
          currentInstallmentIndex
          installmentAmount
          dueDate
          caseNumber
          remainingBalance
          paymentMethod {
            brand
            last4digits
            expiry
          }
          testName
          paymentPlanId
          status
          currency
        }
      }
    }
  `;

  public static getPaymentPlans = (): DocumentNode => gql`
    query GetPaymentPlans(
      $ctsToken: String!
      $input: PaymentPlansRequestInput!
    ) {
      getPaymentPlans(ctsToken: $ctsToken, input: $input) {
        items {
          paymentPlanId
          caseNumber
          currency
          dueDate
          installmentAmount
          paymentMethod {
            brand
            last4digits
            expiry
          }
          remainingBalance
          numInstallments
          currentInstallmentIndex
          status
        }
        lastEvaluatedKey
        totalCount
      }
    }
  `;

  public static updatePayment = (): DocumentNode => gql`
    query UpdatePaymentSetup($ctsToken: String!) {
      updatePaymentSetup(ctsToken: $ctsToken) {
        clientSecret
        customerSessionSecret
      }
    }
  `;

  public static createPayFullBalance = (): DocumentNode => gql`
    query CreatePayFullBalance($ctsToken: String!, $paymentPlanId: String!) {
      createPayFullBalance(ctsToken: $ctsToken, paymentPlanId: $paymentPlanId) {
        clientSecret
        customerSessionSecret
        paymentRequestId
      }
    }
  `;

  // ===== CTS-authenticated mutations =====

  public static createPayment = (): DocumentNode => gql`
    mutation CreatePayment($ctsToken: String!, $input: CreatePaymentInput!) {
      createPayment(ctsToken: $ctsToken, input: $input) {
        clientSecret
        customerSessionSecret
        paymentRequestId
      }
    }
  `;

  public static updatePaymentMethod = (): DocumentNode => gql`
    mutation UpdatePaymentMethod(
      $ctsToken: String!
      $input: UpdatePaymentMethodInput!
    ) {
      updatePaymentMethod(ctsToken: $ctsToken, input: $input)
    }
  `;

  public static confirmPayment = (): DocumentNode => gql`
    mutation ConfirmPayment($ctsToken: String!, $input: ConfirmPaymentInput!) {
      confirmPayment(ctsToken: $ctsToken, input: $input)
    }
  `;

  public static createPaymentPlan = (): DocumentNode => gql`
    mutation CreatePaymentPlan(
      $ctsToken: String!
      $input: CreatePaymentPlanInput!
    ) {
      createPaymentPlan(ctsToken: $ctsToken, input: $input) {
        clientSecret
        customerSessionSecret
        paymentRequestId
      }
    }
  `;

  public static confirmPaymentPlan = (): DocumentNode => gql`
    mutation ConfirmPaymentPlan(
      $ctsToken: String!
      $input: ConfirmPaymentPlanInput!
    ) {
      confirmPaymentPlan(ctsToken: $ctsToken, input: $input)
    }
  `;

  public static confirmPayFullBalance = (): DocumentNode => gql`
    mutation ConfirmPayFullBalance(
      $ctsToken: String!
      $input: ConfirmPayFullBalanceInput!
    ) {
      confirmPayFullBalance(ctsToken: $ctsToken, input: $input)
    }
  `;

  public static getStatements = (): DocumentNode => gql`
    mutation GetStatements($ctsToken: String!, $input: HistoryRequestInput!) {
      getStatements(ctsToken: $ctsToken, input: $input) {
        totalCount
        items {
          bosOrderUid
          bosLineItemUid
          statementUid
          testType
          balance
          caseNumber
          dateOfService
          orderedBy
          hasPDF
          paidStatus
          currency
        }
      }
    }
  `;

  public static getPaymentsHistory = (): DocumentNode => gql`
    mutation GetPaymentsHistory(
      $ctsToken: String!
      $input: HistoryRequestInput!
    ) {
      getPaymentsHistory(ctsToken: $ctsToken, input: $input) {
        totalCount
        items {
          bosPaymentObjectId
          lineItemUid
          testName {
            fullName
            shortName
          }
          caseNumber
          paidDate
          dateOfService
          paymentMethod {
            brand
            last4digits
            expiry
          }
          amount
          currency
          receiptId
          upsPaymentId
        }
      }
    }
  `;

  public static getPaymentPlanPreview = (): DocumentNode => gql`
    mutation GetPaymentPlanPreview(
      $ctsToken: String!
      $input: PaymentPlanPreviewInput!
    ) {
      getPaymentPlanPreview(ctsToken: $ctsToken, input: $input) {
        items {
          indexes
          amount
        }
      }
    }
  `;

  public static getPaymentPlanDetail = (): DocumentNode => gql`
    mutation GetPaymentPlanDetail(
      $ctsToken: String!
      $input: PaymentPlanDetailInput!
    ) {
      getPaymentPlanDetail(ctsToken: $ctsToken, input: $input) {
        currency
        currentInstallmentIndex
        numberOfInstallments
        orderTotal
        paidToDate
        paymentDate
        paymentMethodId {
          brand
          last4digits
          expiry
        }
        planStartDate
        remainingBalance
        testName
        numLineItems
        installments {
          amount
          dueDate
          index
          status
          paidDate
        }
        status
        cancellationDate
        paidFullBalance
        paidLater
      }
    }
  `;

  public static submitSurvey = (): DocumentNode => gql`
    mutation SubmitPostbillSurvey(
      $ctsToken: String!
      $input: SubmitSurveyInput!
    ) {
      submitPostbillSurvey(ctsToken: $ctsToken, input: $input) {
        message
      }
    }
  `;
}

export default PostBillService;
